/**
 * LoginForm.js — Commerce7 in-chat login form (message kind "login-form"),
 * Potter's Vineyard variant (ported from Lightning Rock Winery).
 *
 * Parity with old showLoginForm (RedConcierge/static/javascript/main.js 2060):
 * title "Login" + "Please enter your details", an Email field, a Password
 * field with a show/hide eye toggle, inline error slots, a Login button, a
 * "Sign up" link, and a "Continue without login" action.
 *
 * ADAPTED FOR THE NEW WIDGET: the old form reloaded the host page to
 * /profile/ after login (onLogin, main.js 4065). The embeddable widget can't
 * do that, so submission is event-driven: emits ws-login-submit
 * {email, password}; features/optional/account.js owns the Commerce7 call,
 * stores the token, and shows the greeting in-chat. Errors come back via
 * ws-login-error {message, field}, rendered inline here.
 *
 * EVENTS OUT: ws-login-submit {email, password}, ws-login-skip
 * EVENTS IN:  ws-login-error {message, field}, ws-login-success
 */

import { emit, on } from "../../../core/state.js";
import { getTheme, getContact } from "../../../core/configAccessors.js";

const EYE_OPEN =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
const EYE_OFF =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class LoginForm {
  render() {
    const wrap = document.createElement("div");
    wrap.className = "ws-vp-login";

    const head = document.createElement("div");
    head.className = "ws-vp-login-head";
    const title = document.createElement("p");
    title.className = "ws-vp-login-title";
    title.textContent = "Login";
    const desc = document.createElement("div");
    desc.className = "ws-vp-login-desc";
    desc.textContent = "Please enter your details";
    head.appendChild(title);
    head.appendChild(desc);
    wrap.appendChild(head);

    // Email field
    const emailGroup = document.createElement("div");
    emailGroup.className = "ws-vp-field";
    const emailLabel = document.createElement("label");
    emailLabel.textContent = "Email";
    const email = document.createElement("input");
    email.type = "email";
    email.className = "ws-vp-input ws-vp-email";
    email.placeholder = "Enter your email";
    const emailErr = document.createElement("small");
    emailErr.className = "ws-vp-field-error";
    emailGroup.appendChild(emailLabel);
    emailGroup.appendChild(email);
    emailGroup.appendChild(emailErr);
    wrap.appendChild(emailGroup);

    // Password field with eye toggle
    const pwdGroup = document.createElement("div");
    pwdGroup.className = "ws-vp-field";
    const pwdLabel = document.createElement("label");
    pwdLabel.textContent = "Password";
    const pwdRow = document.createElement("div");
    pwdRow.className = "ws-vp-pwd-row";
    const pwd = document.createElement("input");
    pwd.type = "password";
    pwd.className = "ws-vp-input ws-vp-pwd";
    pwd.placeholder = "Enter your password";
    const eye = document.createElement("span");
    eye.className = "ws-vp-eye";
    eye.innerHTML = EYE_OFF;
    eye.addEventListener("click", () => {
      const show = pwd.type === "password";
      pwd.type = show ? "text" : "password";
      eye.innerHTML = show ? EYE_OPEN : EYE_OFF;
    });
    pwdRow.appendChild(pwd);
    pwdRow.appendChild(eye);
    const pwdErr = document.createElement("small");
    pwdErr.className = "ws-vp-field-error";
    pwdGroup.appendChild(pwdLabel);
    pwdGroup.appendChild(pwdRow);
    pwdGroup.appendChild(pwdErr);
    wrap.appendChild(pwdGroup);

    // Footer: form-level error, Login button, sign-up + skip links.
    const footer = document.createElement("div");
    footer.className = "ws-vp-login-footer";
    const formErr = document.createElement("small");
    formErr.className = "ws-vp-form-error";
    footer.appendChild(formErr);

    const submit = document.createElement("button");
    submit.type = "button";
    submit.className = "ws-vp-login-submit";
    submit.textContent = "Login";

    const doSubmit = () => {
      emailErr.textContent = "";
      formErr.textContent = "";
      const emailVal = (email.value || "").trim();
      const pwdVal = (pwd.value || "").trim();
      if (!EMAIL_RE.test(emailVal)) {
        emailErr.textContent = "Enter valid email address";
        email.focus();
        return;
      }
      submit.disabled = true;
      submit.innerHTML = '<span class="ws-vp-login-spinner" aria-hidden="true"></span>';
      emit("ws-login-submit", { email: emailVal, password: pwdVal });
    };
    submit.addEventListener("click", doSubmit);
    pwd.addEventListener("keydown", (e) => { if (e.key === "Enter") doSubmit(); });
    footer.appendChild(submit);

    // "Don't have an account? Sign up" — old showLoginForm ALWAYS rendered
    // this, linking to the winery's Commerce7 account page
    // (${originURL}profile/create-account). Use account_register_url if the
    // admin sets one, else derive it from the winery website.
    const websiteBase = (getContact("website") || "").replace(/\/+$/, "");
    const registerUrl =
      getTheme("account_register_url", null) ||
      (websiteBase ? `${websiteBase}/profile/create-account` : null);
    const signup = document.createElement("p");
    signup.className = "ws-vp-login-hint";
    signup.append("Don’t have an account? ");
    const a = document.createElement("a");
    a.href = registerUrl || "#";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = "Sign up";
    signup.appendChild(a);
    footer.appendChild(signup);

    const skip = document.createElement("p");
    skip.className = "ws-vp-login-skip";
    skip.textContent = "Continue without login";
    skip.addEventListener("click", () => {
      // account.js's ws-login-skip handler removes this + every other
      // logged-out-flow message (old skipLoginForm parity) via removeMessage,
      // so the persisted transcript stays in sync too — no manual DOM removal
      // needed here.
      emit("ws-login-skip");
    });
    footer.appendChild(skip);

    wrap.appendChild(footer);

    // Inline error feedback driven by account.js after a failed login.
    on("ws-login-error", ({ message, field } = {}) => {
      if (!wrap.isConnected) return;
      submit.disabled = false;
      submit.textContent = "Login";
      if (field === "email") emailErr.textContent = message || "Invalid email";
      else formErr.textContent = message || "Something went wrong. Please try again.";
    });

    // On success, account.js's ws-login-success handler clears this + every
    // other logged-out-flow message via removeMessage — nothing to do here.

    return wrap;
  }
}
